































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { CvValue, FieldMetadataAccount } from '@/models'
import { loadAccountMetadata, FMAT_BOOL } from '@/utils/account-metadata'

/**
 * Editable form for the account's dynamic, company-scoped metadata fields (defined in
 * field_metadata_account), bound to the account's `meta` object via v-model.
 * Emits `validity` (boolean) whenever the required-field validity changes.
 */
@Component
export default class AccountMetadataForm extends Vue {
  @Prop({ default: '' }) companyId!: string
  @Prop({ default: () => ({}) }) value!: Record<string, any>

  private fields: FieldMetadataAccount[] = []
  private selectOptions: Record<string, CvValue[]> = {}
  private loading = false
  private loadError = false

  get metaModel(): Record<string, any> {
    return this.value || {}
  }

  @Watch('companyId')
  async onCompanyChange() {
    await this.loadFields()
  }

  async mounted() {
    if (this.companyId) {
      await this.loadFields()
    }
  }

  private async loadFields() {
    if (!this.companyId) {
      this.fields = []
      this.selectOptions = {}
      this.loadError = false
      this.emitValidity()
      return
    }
    try {
      this.loading = true
      this.loadError = false
      const { fields, selectOptions } = await loadAccountMetadata(this.companyId)
      this.fields = fields
      this.selectOptions = selectOptions
    } catch (error) {
      console.error('Error loading account metadata fields:', error)
      this.fields = []
      this.selectOptions = {}
      this.loadError = true
    } finally {
      this.loading = false
      this.emitValidity()
    }
  }

  optionsFor(field: FieldMetadataAccount): CvValue[] {
    return (field.optionsId && this.selectOptions[field.optionsId]) || []
  }

  fieldLabel(field: FieldMetadataAccount): string {
    return (field.label || field.fieldCode) + (field.required ? ' *' : '')
  }

  private isEmpty(value: any): boolean {
    return value === undefined || value === null || value === ''
  }

  // Required validation does not apply to booleans (a checkbox is always a valid value).
  private isFieldValid(field: FieldMetadataAccount, meta: Record<string, any> = this.metaModel): boolean {
    if (!field.required || field.typeId === FMAT_BOOL) return true
    return !this.isEmpty(meta[field.fieldCode])
  }

  // false marks the input red; null keeps it neutral (only flag genuine missing-required).
  fieldState(field: FieldMetadataAccount): boolean | null {
    return this.isFieldValid(field) ? null : false
  }

  private emitValidity(meta: Record<string, any> = this.metaModel) {
    this.$emit('validity', this.fields.every(field => this.isFieldValid(field, meta)))
  }

  onChange(fieldCode: string, value: any) {
    const meta = { ...this.metaModel, [fieldCode]: value }
    this.$emit('input', meta)
    this.emitValidity(meta)
  }
}
